import { useState } from 'react'
import { useMutation } from 'urql'
import { gql } from '../gql/gql'
import { Button } from './ui/button'
import { Label } from './ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'

const SUBMIT_FEEDBACK_MUTATION = gql(`
  mutation SubmitFeedback($message: String!) {
    submitFeedback(message: $message)
  }
`)

interface FeedbackDialogProps {
  children?: React.ReactNode
}

export function FeedbackDialog({ children }: FeedbackDialogProps) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const [{ fetching }, submitFeedback] = useMutation(SUBMIT_FEEDBACK_MUTATION)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const result = await submitFeedback({ message })
    if (result.error) {
      setError('Failed to send feedback. Please try again.')
      return
    }
    setSuccess(true)
    setTimeout(() => setOpen(false), 1200)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setMessage('')
      setError('')
      setSuccess(false)
    }
    setOpen(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>Bug, suggestion, anything — we read every message.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          {error && <p className="text-sm text-destructive">{error}</p>}
          {success && <p className="text-sm text-green-600">Thanks! Feedback sent.</p>}
          <div className="space-y-1">
            <Label htmlFor="feedback">Message</Label>
            <textarea
              id="feedback"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind?"
              rows={5}
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </div>
          <Button type="submit" className="w-full" disabled={fetching || !message.trim()}>
            {fetching ? 'Sending...' : 'Send'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
